import { mergeAttributes, Node } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';

import type { VideoPlaybackResolver, VideoThumbnails, VideoUpdate, VideoUploadLimits } from '../../../../types';
import { VideoView } from './views/VideoView';

// Networking stays in the host: the node subscribes to live updates through
// subscribeToStatus (returns an unsubscribe) and asks the host to resubmit a
// failed transcode through onRetry. resolvePlayback lets a rendered (non-editing)
// ready video play. loadThumbnails, selectThumbnail, and uploadThumbnail read and
// change the owner's thumbnail. onUnplayableChange tells the composer when the video
// failed or was blocked, so it can hold the post back, and uploadLimits and locale fill in why.
// All are optional so hosts that only render bodies keep working; without them the
// card shows a neutral placeholder.
export interface VideoOptions {
  subscribeToStatus?: (videoId: string, onUpdate: (update: VideoUpdate) => void) => () => void;
  onRetry?: (videoId: string) => Promise<void>;
  resolvePlayback?: VideoPlaybackResolver;
  loadThumbnails?: (videoId: string) => Promise<VideoThumbnails | null>;
  selectThumbnail?: (videoId: string, index: number) => Promise<VideoThumbnails | null>;
  uploadThumbnail?: (videoId: string, file: File) => Promise<VideoThumbnails | null>;
  onUnplayableChange?: (unplayable: boolean) => void;
  uploadLimits?: VideoUploadLimits;
  locale?: string;
  translations?: Record<string, string>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    video: {
      // Insert the native video, replacing any existing one (replacement = new id).
      setVideo: (attrs: { videoId: string }) => ReturnType;
      // Insert a placeholder (videoId: null) at pos, or the selection when omitted,
      // replacing any existing video. The upload fills in the id when it confirms.
      insertProvisionalVideo: (pos?: number) => ReturnType;
      // Set the confirmed id on the single video node in place, keeping its position.
      setVideoId: (videoId: string) => ReturnType;
      // Remove the placeholder when its upload is canceled.
      removeProvisionalVideo: () => ReturnType;
    };
  }
}

// The placeholder follows the upload, not the author's edits, so its insert, fill,
// and removal stay out of undo history: undo must never revive a placeholder whose
// upload is gone or blank out a confirmed id.
const skipHistory = (tr: import('@tiptap/pm/state').Transaction) => tr.setMeta('addToHistory', false);

const videoPositions = (doc: import('@tiptap/pm/model').Node, name: string): number[] => {
  const positions: number[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name === name) positions.push(pos);
  });
  return positions;
};

export const Video = Node.create<VideoOptions>({
  name: 'video',

  addOptions() {
    return {};
  },

  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      videoId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-video-id'),
        renderHTML: (attributes) => (attributes.videoId ? { 'data-video-id': attributes.videoId } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': this.name })];
  },

  addCommands() {
    const name = this.name;
    const dropExisting = (tr: import('@tiptap/pm/state').Transaction) => {
      for (const pos of videoPositions(tr.doc, name).reverse()) {
        tr.delete(pos, pos + 1);
      }
    };

    return {
      setVideo:
        (attrs) =>
        ({ tr, dispatch }) => {
          if (!attrs.videoId) return false;
          // Drop existing videos before inserting so a replacement lands as the only one.
          dropExisting(tr);
          tr.replaceSelectionWith(this.type.create(attrs));
          if (dispatch) dispatch(tr);
          return true;
        },

      insertProvisionalVideo:
        (pos) =>
        ({ tr, dispatch }) => {
          dropExisting(tr);
          const node = this.type.create({ videoId: null });
          // Insert at the requested drop point, or replace the selection when omitted.
          if (typeof pos === 'number') {
            tr.insert(tr.mapping.map(pos), node);
          } else {
            tr.replaceSelectionWith(node);
          }
          skipHistory(tr);
          if (dispatch) dispatch(tr);
          return true;
        },

      setVideoId:
        (videoId) =>
        ({ tr, dispatch }) => {
          if (!videoId) return false;
          const positions = videoPositions(tr.doc, name);
          if (positions.length === 0) return false;
          // Single-video is enforced, so the first (only) node is the placeholder.
          tr.setNodeAttribute(positions[0], 'videoId', videoId);
          skipHistory(tr);
          if (dispatch) dispatch(tr);
          return true;
        },

      removeProvisionalVideo:
        () =>
        ({ tr, dispatch }) => {
          const positions = videoPositions(tr.doc, name).filter((pos) => !tr.doc.nodeAt(pos)?.attrs.videoId);
          if (positions.length === 0) return false;
          for (const pos of positions.reverse()) {
            tr.delete(pos, pos + 1);
          }
          skipHistory(tr);
          if (dispatch) dispatch(tr);
          return true;
        },
    };
  },

  // Safety net for paste or programmatic content: keep only the first video node.
  addProseMirrorPlugins() {
    const name = this.name;
    return [
      new Plugin({
        appendTransaction: (_transactions, _oldState, newState) => {
          const positions = videoPositions(newState.doc, name);
          if (positions.length < 2) return null;
          const tr = newState.tr;
          for (const pos of positions.slice(1).reverse()) {
            tr.delete(pos, pos + 1);
          }
          return tr;
        },
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VideoView);
  },
});

export default Video;
